import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Paper,
  Grid,
  Avatar,
  Card,
  CardContent,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { getAllUser, updateUser, deleteUser } from '../api/user-api';
import { PolarArea } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

const RoleManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [role, setRole] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const userData = await getAllUser();
        setUsers(userData);
      } catch (error) {
        console.error("Error fetching users:", error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleOpenDialog = (user) => {
    setSelectedUser(user);
    setRole(user.role);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setRole('');
  };

  const handleOpenDeleteDialog = (user) => {
    setSelectedUser(user);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedUser(null);
  };

  const handleRoleChange = (event) => {
    setRole(event.target.value);
  };

  const handleSaveRole = async () => {
    if (selectedUser) {
      try {
        await updateUser(selectedUser.id, { role });
        setUsers(users.map(user =>
          user.id === selectedUser.id ? { ...user, role } : user
        ));
        handleCloseDialog();
      } catch (error) {
        console.error("Error updating user role:", error.message);
      }
    }
  };

  const handleDeleteUser = async () => {
    if (selectedUser) {
      try {
        await deleteUser(selectedUser.id);
        setUsers(users?.filter(user => user.id !== selectedUser.id));
        handleCloseDeleteDialog();
      } catch (error) {
        console.error("Error deleting user:", error.message);
      }
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate user counts by role
  const userCounts = users.reduce((acc, user) => {
    acc[user.role] = (acc[user.role] || 0) + 1; // Increment count based on role
    return acc;
  }, { admin: 0, faculty: 0, student: 0 });

  const totalUsers = users.length;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        User Management
      </Typography>

      {loading ? (
        <CircularProgress color="primary" />
      ) : (
        <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Email</strong></TableCell>
                <TableCell><strong>Role</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Grid container alignItems="center">
                      <Grid item>
                        <Avatar sx={{ mr: 2, bgcolor: " #1976d2", color: 'white' }}>
                          {user.name[0]}
                        </Avatar>
                      </Grid>
                      <Grid item>{user.name}</Grid>
                    </Grid>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>{user.status}</TableCell>
                  <TableCell>
                    <Tooltip title="Edit Role">
                      <IconButton color="primary" onClick={() => handleOpenDialog(user)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete User">
                      <IconButton color="error" onClick={() => handleOpenDeleteDialog(user)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={users.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Role Assignment Dialog */}
      {selectedUser && (
        <Dialog open={openDialog} onClose={handleCloseDialog}>
          <DialogTitle>Change Role for {selectedUser.name}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Role"
              value={role}
              onChange={handleRoleChange}
              select
              SelectProps={{
                native: true,
              }}
            >
              <option value="admin">Admin</option>
              <option value="student">Student</option>
              <option value="faculty">Faculty</option>
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">Cancel</Button>
            <Button onClick={handleSaveRole} color="primary">Save</Button>
          </DialogActions>
        </Dialog>
      )}
     
      {/* User Analytics Polar Area Chart */}
      {/* <Card sx={{ mb: 4, width: "30vw",height: "auto" }}>
        <CardContent>
          <Typography variant="h6">User Distribution by Role</Typography>
          <PolarArea
            data={{
              labels: ['Admin', 'Faculty', 'Students'],
              datasets: [
                {
                  label: 'Total Users',
                  data: [
                    users.filter(user => user.role === 'admin').length,
                    users.filter(user => user.role === 'faculty').length,
                    users.filter(user => user.role === 'student').length,
                  ],
                  backgroundColor: ['rgba(25, 118, 210, 0.6)', 'rgba(76, 175, 80, 0.6)', 'rgba(255, 193, 7, 0.6)'],
                },
              ],
            }}
          />
        </CardContent>
      </Card> */}
      {/* Delete Confirmation Dialog */}
      {selectedUser && (
        <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete {selectedUser.name}?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog} color="primary">Cancel</Button>
            <Button onClick={handleDeleteUser} color="error">Delete</Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
};

export default RoleManagementPage;
